import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  useAllDecksForFilterSideBarQuery,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  useAllWorkpacksForFilterSideBarQuery,
  useAllAssemblyTemplatesForFilterSideBarQuery,
} from '@/__generated__/graphql';
import type {
  AllDecksForFilterSideBarQuery,
  AllPaintRegionsForFilterAndSearchPanelQuery,
  AllWorkpacksForFilterSideBarQuery,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { FilterField, FilterPill } from './InsightsSearchBar.types';
import { buildFilterFields, buildFilterPills } from './utils';
import type { TemplateFilterInput, AssemblyTemplateQuery } from '@/types';

export function useFilterData(structureId: string) {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const unitSystem = useAtomValue(state.unitSystem);
  const structurePipesByStructure = useAtomValue(state.structurePipesByStructureId);
  const structurePipes = structureId ? structurePipesByStructure[structureId] : null;

  const { data: decksData } = useAllDecksForFilterSideBarQuery({
    variables: { structureId },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const { data: paintRegionsData } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: { input: { structureId } },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const { data: workpacksData } = useAllWorkpacksForFilterSideBarQuery({
    variables: { structureId, hasPois: true },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const filterFields = useMemo<FilterField[]>(() => {
    return buildFilterFields({
      assemblyTemplate,
      decksData,
      paintRegionsData,
      workpacksData,
      unitSystem,
      structurePipes,
    });
  }, [assemblyTemplate, decksData, paintRegionsData, workpacksData, unitSystem, structurePipes]);

  return {
    filterFields,
    decksData,
    paintRegionsData,
    workpacksData,
    assemblyTemplate,
    unitSystem,
    structurePipes,
  };
}

export function useBuildFilterPills(
  selectedTemplateFilters: TemplateFilterInput[],
  selectedDeckIds: string[],
  selectedPaintRegionIds: string[],
  selectedWorkpackIds: string[],
  assemblyTemplate: AssemblyTemplateQuery | undefined,
  decksData: AllDecksForFilterSideBarQuery | undefined,
  paintRegionsData: AllPaintRegionsForFilterAndSearchPanelQuery | undefined,
  workpacksData: AllWorkpacksForFilterSideBarQuery | undefined,
  unitSystem: UnitSystemEnum,
  structurePipes: Record<string, number> | null | undefined
): FilterPill[] {
  return useMemo(() => {
    return buildFilterPills({
      selectedTemplateFilters,
      selectedDeckIds,
      selectedPaintRegionIds,
      selectedWorkpackIds,
      assemblyTemplate,
      decksData,
      paintRegionsData,
      workpacksData,
      unitSystem,
      structurePipes,
    });
  }, [
    selectedTemplateFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    assemblyTemplate,
    decksData,
    paintRegionsData,
    workpacksData,
    unitSystem,
    structurePipes,
  ]);
}
